import { useState, useRef, useEffect } from 'react';
import { Plus, X, ChevronDown } from 'lucide-react';
import Input from '@/components/common/Input';
import Select from '@/components/common/Select';
import Button from '@/components/common/Button';
import { useAccounts } from '@/hooks/useAccounts';
import { useCreateRule, useUpdateRule } from '@/hooks/useRules';

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does Not Contain' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
];

const CONDITION_FIELDS = [
  { value: 'from', label: 'From Address' },
  { value: 'to', label: 'To Address' },
  { value: 'subject', label: 'Subject' },
  { value: 'body', label: 'Body' },
];

const ACTION_TYPES = [
  { value: 'set_category', label: 'Set Category' },
  { value: 'set_priority', label: 'Set Priority' },
  { value: 'star', label: 'Star Email' },
  { value: 'archive', label: 'Archive Email' },
  { value: 'mark_read', label: 'Mark as Read' },
  { value: 'set_action_needed', label: 'Flag Action Needed' },
];

const CATEGORIES = ['important', 'work', 'personal', 'finance', 'social', 'promotional', 'newsletter', 'receipt', 'notification', 'spam', 'other'];
const PRIORITIES = ['critical', 'high', 'medium', 'low'];

function isGroup(item) {
  return item && item.match_type && Array.isArray(item.conditions);
}

const defaultCondition = () => ({ field: 'from', operator: 'contains', value: '' });
const defaultGroup = (matchType) => ({ match_type: matchType, conditions: [defaultCondition()] });

function ConditionRow({ condition, onChange, onRemove, showRemove }) {
  return (
    <div className="flex items-end gap-2 p-3 border rounded-lg bg-background">
      <Select
        label="Field"
        value={condition.field}
        onChange={(e) => onChange('field', e.target.value)}
        className="flex-1"
      >
        {CONDITION_FIELDS.map((f) => (
          <option key={f.value} value={f.value}>{f.label}</option>
        ))}
      </Select>
      <Select
        label="Operator"
        value={condition.operator}
        onChange={(e) => onChange('operator', e.target.value)}
        className="flex-1"
      >
        {OPERATORS.map((op) => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </Select>
      <Input
        label="Value"
        value={condition.value}
        onChange={(e) => onChange('value', e.target.value)}
        placeholder="Enter value..."
        className="flex-1"
      />
      {showRemove && (
        <button type="button" onClick={onRemove}
          className="mb-2 p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function SubConditionGroup({ group, onChange, onRemove, showRemove, isRoot, parentMatchType }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  const updateItem = (index, value) => {
    const next = [...group.conditions];
    next[index] = value;
    onChange('conditions', next);
  };

  const removeItem = (index) => {
    const next = group.conditions.filter((_, i) => i !== index);
    onChange('conditions', next.length ? next : [defaultCondition()]);
  };

  const addCondition = () => {
    onChange('conditions', [...group.conditions, defaultCondition()]);
    setShowDropdown(false);
  };

  const addGroup = () => {
    const childMatchType = parentMatchType === 'all' ? 'any' : 'all';
    onChange('conditions', [...group.conditions, defaultGroup(childMatchType)]);
    setShowDropdown(false);
  };

  const toggleMatchType = () => {
    onChange('match_type', group.match_type === 'all' ? 'any' : 'all');
  };

  return (
    <div className={`${isRoot ? '' : 'ml-6 pl-4 border-l-2 border-muted'}`}>
      {/* AND/OR toggle */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className={`text-xs font-medium ${group.match_type === 'all' ? 'text-foreground' : 'text-muted-foreground'}`}>All of</span>
        <button type="button" onClick={toggleMatchType}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${group.match_type === 'all' ? 'bg-primary' : 'bg-muted-foreground'}`}>
          <span className={`inline-block h-4 w-4 rounded-full bg-background transition-transform ${group.match_type === 'all' ? 'translate-x-[2px]' : 'translate-x-[18px]'}`} />
        </button>
        <span className={`text-xs font-medium ${group.match_type === 'any' ? 'text-foreground' : 'text-muted-foreground'}`}>One of</span>
        {showRemove && (
          <button type="button" onClick={onRemove}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors ml-auto">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Items */}
      <div className="space-y-2">
        {group.conditions.map((item, index) => (
          isGroup(item) ? (
            <SubConditionGroup
              key={index}
              group={item}
              onChange={(field, val) => {
                const updated = { ...item, [field]: val };
                updateItem(index, updated);
              }}
              onRemove={() => removeItem(index)}
              showRemove={group.conditions.length > 1}
              isRoot={false}
              parentMatchType={group.match_type}
            />
          ) : (
            <ConditionRow
              key={index}
              condition={item}
              onChange={(field, val) => {
                const updated = { ...item, [field]: val };
                updateItem(index, updated);
              }}
              onRemove={() => removeItem(index)}
              showRemove={group.conditions.length > 1}
            />
          )
        ))}
      </div>

      {/* Add buttons */}
      <div className="flex items-center gap-1 mt-2">
        <Button type="button" size="sm" variant="outline" onClick={addCondition} className="flex items-center gap-1">
          <Plus className="h-3.5 w-3.5" />
          Add Condition
        </Button>
        {isRoot && (
          <div className="relative" ref={dropdownRef}>
            <button type="button" onClick={() => setShowDropdown(!showDropdown)}
              className="p-1.5 rounded-md border border-input hover:bg-muted transition-colors">
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {showDropdown && (
              <div className="absolute right-0 top-full mt-1 z-10 w-44 rounded-md border bg-popover shadow-lg">
                <button type="button" onClick={addCondition}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors">
                  Add Condition
                </button>
                <button type="button" onClick={addGroup}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors border-t border-border">
                  Add Group
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function RuleForm({ rule, onSuccess, onCancel }) {
  const isEditing = !!rule;
  const { data: accounts } = useAccounts();

  const [formData, setFormData] = useState({
    name: rule?.name || '',
    type: rule?.type || 'static',
    enabled: rule?.enabled !== false,
    priority: rule?.priority || 0,
    account_id: rule?.account_id || '',
  });

  const [conditionGroup, setConditionGroup] = useState({
    match_type: rule?.match_type || 'all',
    conditions: Array.isArray(rule?.conditions) && rule.conditions.length
      ? rule.conditions.map(c => isGroup(c) ? c : { ...c })
      : [defaultCondition()],
  });

  const [actions, setActions] = useState(
    Array.isArray(rule?.actions) ? rule.actions : [{ type: 'set_category', value: 'work' }]
  );

  const createMutation = useCreateRule();
  const updateMutation = useUpdateRule();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanNested = (item) => {
      if (isGroup(item)) {
        const cleaned = item.conditions.map(sub => cleanNested(sub)).filter(sub => isGroup(sub) || sub.value);
        return { ...item, conditions: cleaned.length ? cleaned : [defaultCondition()] };
      }
      return item;
    };
    let cleanConditions = conditionGroup.conditions.map(c => cleanNested(c)).filter(c => isGroup(c) || c.value);
    if (cleanConditions.length === 0) {
      cleanConditions = [defaultCondition()];
    }

    const data = {
      ...formData,
      match_type: conditionGroup.match_type,
      conditions: cleanConditions,
      actions: actions.map(a => a.type === 'set_category' || a.type === 'set_priority' ? a : { type: a.type }),
    };

    if (!data.account_id) {
      delete data.account_id;
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: rule.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      onSuccess?.();
    } catch (err) {
      // Error handled by mutation
    }
  };

  const addAction = () => {
    setActions([...actions, { type: 'set_category', value: 'work' }]);
  };

  const removeAction = (index) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const updateAction = (index, field, value) => {
    const newActions = [...actions];
    newActions[index] = { ...newActions[index], [field]: value };
    if (field === 'type' && value !== 'set_category' && value !== 'set_priority') {
      delete newActions[index].value;
    }
    setActions(newActions);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info */}
      <div className="space-y-4">
        <Input
          label="Rule Name"
          placeholder="My Rule"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Rule Type"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          >
            <option value="static">Static Rule</option>
            <option value="ai">AI Rule (Coming Soon)</option>
          </Select>

          <Input
            label="Priority"
            type="number"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
            placeholder="0"
          />
        </div>

        <Select
          label="Apply to Account (Optional)"
          value={formData.account_id}
          onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
        >
          <option value="">All Accounts</option>
          {accounts?.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}
            </option>
          ))}
        </Select>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.enabled}
            onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            className="h-4 w-4 rounded border-input"
          />
          <span className="text-sm">Enable this rule</span>
        </label>
      </div>

      {/* Conditions */}
      <div className="space-y-3">
        <h3 className="font-medium">Conditions</h3>

        <SubConditionGroup
          group={conditionGroup}
          onChange={(field, value) => setConditionGroup(prev => ({ ...prev, [field]: value }))}
          showRemove={false}
          isRoot={true}
          parentMatchType={conditionGroup.match_type}
        />
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">Actions</h3>
          <Button type="button" size="sm" onClick={addAction} className="flex items-center gap-1">
            <Plus className="h-4 w-4" />
            Add Action
          </Button>
        </div>

        {actions.map((action, index) => (
          <div key={index} className="flex items-end gap-2 p-3 border rounded-lg">
            <Select
              label="Action"
              value={action.type}
              onChange={(e) => updateAction(index, 'type', e.target.value)}
              className="flex-1"
            >
              {ACTION_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>

            {(action.type === 'set_category' || action.type === 'set_priority') && (
              <Select
                label="Value"
                value={action.value}
                onChange={(e) => updateAction(index, 'value', e.target.value)}
                className="flex-1"
              >
                {action.type === 'set_category'
                  ? CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))
                  : PRIORITIES.map((pri) => (
                      <option key={pri} value={pri}>
                        {pri}
                      </option>
                    ))}
              </Select>
            )}

            {actions.length > 1 && (
              <button
                type="button"
                onClick={() => removeAction(index)}
                className="mb-2 p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          loading={createMutation.isPending || updateMutation.isPending}
        >
          {isEditing ? 'Update Rule' : 'Create Rule'}
        </Button>
      </div>
    </form>
  );
}
